import React, { useState, useEffect } from 'react';
import { aiJobsAPI } from '../../api/aiJobsApi';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    SparklesIcon,
    ArrowPathIcon,
    ChartBarIcon,
    MapPinIcon,
    TagIcon,
    LightBulbIcon,
    ExclamationTriangleIcon,
    RocketLaunchIcon,
    FaceSmileIcon,
    FaceFrownIcon,
    MinusCircleIcon,
    CalendarDaysIcon,
    DocumentTextIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';

// Klasik Premium Kelime Bulutu Bileşeni
const WordCloud = ({ words }) => {
    if (!words || words.length === 0) return <div className="text-gray-400 text-sm text-center py-8">Veri yok</div>;

    const maxCount = Math.max(...words.map(w => w.count || 1));
    const minCount = Math.min(...words.map(w => w.count || 1));

    // Sade renk paleti - siyah, gri tonları ve vurgu için kırmızı/turuncu
    const colors = [
        'text-gray-900 dark:text-gray-100',     // En önemli - koyu
        'text-red-600 dark:text-red-500',       // Vurgu rengi
        'text-gray-700 dark:text-gray-300',     // Orta
        'text-orange-600 dark:text-orange-500', // Vurgu 2
        'text-gray-600 dark:text-gray-400',     // Orta-açık
        'text-gray-800 dark:text-gray-200',     // Koyu
        'text-amber-700 dark:text-amber-500',   // Vurgu 3
        'text-gray-500 dark:text-gray-500',     // Açık
    ];

    // Kelime boyutunu hesapla (12px - 48px arası)
    const getSize = (count) => {
        const normalized = (count - minCount) / (maxCount - minCount || 1);
        return 12 + normalized * 36;
    };

    // Font ağırlığı - büyük kelimeler daha kalın
    const getWeight = (count) => {
        const normalized = (count - minCount) / (maxCount - minCount || 1);
        if (normalized > 0.7) return 'font-black';
        if (normalized > 0.4) return 'font-bold';
        if (normalized > 0.2) return 'font-semibold';
        return 'font-medium';
    };

    // Bazı kelimeleri dikey göster
    const isVertical = (idx) => [2, 5, 9, 13, 17].includes(idx);

    const getColor = (idx, count) => {
        const normalized = (count - minCount) / (maxCount - minCount || 1);
        // En yüksek değerli kelimeler vurgu rengi alsın
        if (normalized > 0.8) return colors[1]; // Kırmızı
        if (normalized > 0.6) return colors[0]; // Siyah
        return colors[idx % colors.length];
    };

    return (
        <div className="flex flex-wrap gap-x-3 gap-y-2 justify-center items-center p-4 min-h-[180px]">
            {words.slice(0, 25).map((item, idx) => {
                const fontSize = getSize(item.count || 1);
                const weight = getWeight(item.count || 1);
                const color = getColor(idx, item.count || 1);
                const vertical = isVertical(idx);

                return (
                    <span
                        key={idx}
                        className={`
                            ${color} ${weight}
                            transition-all duration-200 ease-out
                            hover:text-indigo-600 dark:hover:text-indigo-400
                            hover:scale-110
                            cursor-default select-none
                            leading-none
                        `}
                        style={{
                            fontSize: `${fontSize}px`,
                            writingMode: vertical ? 'vertical-rl' : 'horizontal-tb',
                            textOrientation: vertical ? 'mixed' : 'initial',
                        }}
                        title={`${item.word}: ${item.count} kez`}
                    >
                        {item.word}
                    </span>
                );
            })}
        </div>
    );
};

// Lokasyon Haritası Bileşeni
const LocationBubbles = ({ locations }) => {
    if (!locations || locations.length === 0) return <div className="text-gray-400 text-sm">Veri yok</div>;

    const maxCount = Math.max(...locations.map(l => l.count || l.mentions || 1));

    return (
        <div className="flex flex-wrap gap-3 justify-center items-end min-h-[120px] py-4">
            {locations.slice(0, 10).map((item, idx) => {
                const count = item.count || item.mentions || 1;
                const size = 40 + (count / maxCount) * 60; // 40-100px arası

                return (
                    <div key={idx} className="flex flex-col items-center group">
                        <div
                            className="rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-bold shadow-lg transition-transform group-hover:scale-110"
                            style={{ width: size, height: size, fontSize: size * 0.25 }}
                            title={`${item.name || item.location}: ${count} kez`}
                        >
                            {count}
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 max-w-[80px] truncate text-center">
                            {item.name || item.location}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const SENTIMENT_COLORS = ['#10B981', '#6B7280', '#EF4444'];

const AIInsightsDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analyzeResult, setAnalyzeResult] = useState(null);
    const [insights, setInsights] = useState(null);
    const [error, setError] = useState(null);

    // Sekmeler için state
    const [activeTab, setActiveTab] = useState('dashboard');
    const [summaries, setSummaries] = useState([]);
    const [loadingSummaries, setLoadingSummaries] = useState(false);

    // Tarih filtreleri - varsayılan son 30 gün
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    const fetchInsights = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await aiJobsAPI.getInsights(startDate, endDate);
            if (response.success) {
                setInsights(response.data);
            } else {
                setError(response.message || 'Veriler alınırken hata oluştu');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateAISummary = async () => {
        setGenerating(true);
        setError(null);
        try {
            const response = await aiJobsAPI.generateSummary(startDate, endDate);
            if (response.success) {
                setInsights(response.data);
            } else {
                setError(response.message || 'Özet oluşturulurken hata oluştu');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    // Kayıtları tek tek analiz et (SalesCallAnalysis tablosuna yazar)
    const analyzeBulkRecords = async () => {
        setAnalyzing(true);
        setError(null);
        setAnalyzeResult(null);
        try {
            const response = await aiJobsAPI.analyzeBulk({
                start_date: startDate,
                end_date: endDate,
                force_reanalyze: false
            });
            if (response.success) {
                setAnalyzeResult(response);
                // Analiz tamamlandıktan sonra insights'ı yenile
                await fetchInsights();
            } else {
                setError(response.message || 'Analiz sırasında hata oluştu');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setAnalyzing(false);
        }
    };

    // Özet kayıtlarını getir
    const fetchSummaries = async () => {
        setLoadingSummaries(true);
        try {
            const response = await aiJobsAPI.getSummaries();
            if (response.success) {
                setSummaries(response.data || []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingSummaries(false);
        }
    };

    // Sekme değiştiğinde veri yükle
    useEffect(() => {
        if (activeTab === 'summaries') {
            fetchSummaries();
        }
    }, [activeTab]);

    useEffect(() => {
        // İlk yüklemede otomatik fetch yapmıyoruz, kullanıcı butona tıklasın
        // fetchInsights();
    }, []);

    // Pie chart için veri hazırla
    const getSentimentPieData = () => {
        if (!insights?.sentiment_distribution) return [];
        return [
            { name: 'Olumlu', value: insights.sentiment_distribution.positive || 0 },
            { name: 'Nötr', value: insights.sentiment_distribution.neutral || 0 },
            { name: 'Olumsuz', value: insights.sentiment_distribution.negative || 0 }
        ];
    };

    // Bar chart için keyword veri hazırla
    const getKeywordBarData = () => {
        if (!insights?.top_keywords) return [];
        return insights.top_keywords.slice(0, 8).map(k => ({
            name: k.word?.substring(0, 12) || k,
            count: k.count || 1
        }));
    };

    // Topic bar chart verisi
    const getTopicBarData = () => {
        if (!insights?.top_topics) return [];
        return insights.top_topics.slice(0, 6).map(t => ({
            name: t.topic?.substring(0, 15) || t,
            count: t.count || 1
        }));
    };

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                            <SparklesIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Insights Dashboard</h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400">B2B Meetings & Activities semantik analizi</p>
                        </div>
                    </div>

                    {/* Tarih Filtreleri */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <span className="text-gray-500">-</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>

                        <button
                            onClick={fetchInsights}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Yenile
                        </button>

                        <button
                            onClick={analyzeBulkRecords}
                            disabled={analyzing}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <ChartBarIcon className={`w-4 h-4 ${analyzing ? 'animate-pulse' : ''}`} />
                            {analyzing ? 'Analiz Ediliyor...' : 'Kayıtları Analiz Et'}
                        </button>

                        <button
                            onClick={generateAISummary}
                            disabled={generating}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <SparklesIcon className={`w-4 h-4 ${generating ? 'animate-pulse' : ''}`} />
                            {generating ? 'Özet Oluşturuluyor...' : 'AI Özet Oluştur'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Analyze Result */}
            {analyzeResult && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
                    <strong>✓ Analiz Tamamlandı!</strong> {analyzeResult.analyzed_count} kayıt analiz edildi
                    {analyzeResult.total_requested > analyzeResult.analyzed_count &&
                        ` (${analyzeResult.total_requested - analyzeResult.analyzed_count} kayıt daha önce analiz edilmişti)`
                    }
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <ArrowPathIcon className="w-8 h-8 text-indigo-600 animate-spin" />
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Veriler yükleniyor...</span>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                <nav className="flex gap-4" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'dashboard'
                            ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <ChartBarIcon className="w-5 h-5" />
                        Dashboard
                    </button>
                    <button
                        onClick={() => setActiveTab('summaries')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'summaries'
                            ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <DocumentTextIcon className="w-5 h-5" />
                        Dönem Özetleri ({summaries.length})
                    </button>
                </nav>
            </div>

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
                <>
                    {/* No Data */}
                    {!loading && !insights && (
                        <div className="text-center py-20">
                            <SparklesIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz analiz yapılmadı</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-4">
                                1. Önce <strong>"Kayıtları Analiz Et"</strong> butonuna tıklayarak her kaydı ayrı ayrı analiz edin.<br />
                                2. Sonra <strong>"AI Özet Oluştur"</strong> butonuna tıklayarak dönemsel özet oluşturun.
                            </p>
                        </div>
                    )}

                    {/* Dashboard Content */}
                    {!loading && insights && (
                        <div className="space-y-6">
                            {/* Stats Row - Enhanced */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                {/* Total Calls - Compact */}
                                <div className="md:col-span-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg text-white">
                                    <p className="text-sm text-blue-100">Toplam Görüşme</p>
                                    <p className="text-4xl font-bold mt-2">{insights.total_calls || 0}</p>
                                    <div className="mt-2 flex items-center gap-1 text-blue-200 text-xs">
                                        <CalendarDaysIcon className="w-4 h-4" />
                                        {startDate} - {endDate}
                                    </div>
                                </div>

                                {/* Avg Sentiment - Compact */}
                                <div className={`md:col-span-2 rounded-xl p-5 shadow-lg text-white ${insights.avg_sentiment >= 0.3 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                                    insights.avg_sentiment <= -0.3 ? 'bg-gradient-to-br from-red-500 to-rose-600' :
                                        'bg-gradient-to-br from-gray-500 to-gray-600'
                                    }`}>
                                    <p className="text-sm opacity-80">Ortalama Duygu</p>
                                    <p className="text-4xl font-bold mt-2">{insights.avg_sentiment?.toFixed(2) || '0.00'}</p>
                                    <div className="mt-2 flex items-center gap-1 text-xs opacity-80">
                                        {insights.avg_sentiment >= 0.3 ? <FaceSmileIcon className="w-4 h-4" /> :
                                            insights.avg_sentiment <= -0.3 ? <FaceFrownIcon className="w-4 h-4" /> :
                                                <MinusCircleIcon className="w-4 h-4" />}
                                        {insights.avg_sentiment >= 0.3 ? 'Olumlu' : insights.avg_sentiment <= -0.3 ? 'Olumsuz' : 'Nötr'}
                                    </div>
                                </div>

                                {/* Sentiment Pie Chart */}
                                <div className="md:col-span-4 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">Duygu Dağılımı</p>
                                    <div className="flex items-center justify-center">
                                        <ResponsiveContainer width="100%" height={140}>
                                            <PieChart>
                                                <Pie
                                                    data={getSentimentPieData()}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={35}
                                                    outerRadius={60}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                >
                                                    {getSentimentPieData().map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={SENTIMENT_COLORS[index % SENTIMENT_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => `${value}%`} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="space-y-2 ml-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-green-500" />
                                                <span className="text-xs text-gray-600 dark:text-gray-400">Olumlu {insights.sentiment_distribution?.positive || 0}%</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-gray-500" />
                                                <span className="text-xs text-gray-600 dark:text-gray-400">Nötr {insights.sentiment_distribution?.neutral || 0}%</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                                <span className="text-xs text-gray-600 dark:text-gray-400">Olumsuz {insights.sentiment_distribution?.negative || 0}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Keywords Bar Chart */}
                                <div className="md:col-span-4 bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">En Çok Geçen Kelimeler</p>
                                    <ResponsiveContainer width="100%" height={140}>
                                        <BarChart data={getKeywordBarData()} layout="vertical">
                                            <XAxis type="number" hide />
                                            <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Word Cloud & Location Bubbles Row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Word Cloud */}
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-2 mb-4">
                                        <TagIcon className="w-5 h-5 text-indigo-600" />
                                        <h3 className="font-semibold text-gray-900 dark:text-white">Kelime Bulutu</h3>
                                    </div>
                                    <WordCloud words={insights.top_keywords} colorScheme="indigo" />
                                </div>

                                {/* Location Bubbles */}
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-2 mb-4">
                                        <GlobeAltIcon className="w-5 h-5 text-green-600" />
                                        <h3 className="font-semibold text-gray-900 dark:text-white">Bahsedilen Lokasyonlar</h3>
                                    </div>
                                    <LocationBubbles locations={insights.top_locations} />
                                </div>
                            </div>

                            {/* Topics Bar Chart */}
                            {insights.top_topics?.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-2 mb-4">
                                        <LightBulbIcon className="w-5 h-5 text-yellow-600" />
                                        <h3 className="font-semibold text-gray-900 dark:text-white">Tartışılan Konular</h3>
                                    </div>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={getTopicBarData()}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* AI Summary & Insights - Enhanced */}
                            {insights.ai_summary && (
                                <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl p-1 shadow-lg">
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg">
                                                <SparklesIcon className="w-5 h-5 text-white" />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">AI Yönetici Özeti</h3>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base">{insights.ai_summary}</p>
                                    </div>
                                </div>
                            )}

                            {/* Trends, Concerns, Opportunities */}
                            {(insights.emerging_trends?.length > 0 || insights.concerns?.length > 0 || insights.opportunities?.length > 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Emerging Trends */}
                                    {insights.emerging_trends?.length > 0 && (
                                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2 mb-4">
                                                <ChartBarIcon className="w-5 h-5 text-blue-600" />
                                                <h3 className="font-semibold text-gray-900 dark:text-white">Ortaya Çıkan Trendler</h3>
                                            </div>
                                            <ul className="space-y-2">
                                                {insights.emerging_trends.map((trend, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                        <span className="text-blue-500">•</span>
                                                        {trend}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Concerns */}
                                    {insights.concerns?.length > 0 && (
                                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2 mb-4">
                                                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                                                <h3 className="font-semibold text-gray-900 dark:text-white">Dikkat Edilmesi Gerekenler</h3>
                                            </div>
                                            <ul className="space-y-2">
                                                {insights.concerns.map((concern, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                        <span className="text-red-500">•</span>
                                                        {concern}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Opportunities */}
                                    {insights.opportunities?.length > 0 && (
                                        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2 mb-4">
                                                <RocketLaunchIcon className="w-5 h-5 text-green-600" />
                                                <h3 className="font-semibold text-gray-900 dark:text-white">Fırsatlar</h3>
                                            </div>
                                            <ul className="space-y-2">
                                                {insights.opportunities.map((opportunity, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                        <span className="text-green-500">•</span>
                                                        {opportunity}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Summaries Tab */}
            {activeTab === 'summaries' && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-white">Dönem Özetleri</h3>
                        <button
                            onClick={fetchSummaries}
                            disabled={loadingSummaries}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${loadingSummaries ? 'animate-spin' : ''}`} />
                            Yenile
                        </button>
                    </div>

                    {loadingSummaries ? (
                        <div className="flex items-center justify-center py-10">
                            <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
                        </div>
                    ) : summaries.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                            Henüz dönem özeti oluşturulmamış
                        </div>
                    ) : (
                        summaries.map((summary) => (
                            <div key={summary.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {summary.period_start} - {summary.period_end}
                                        </h4>
                                        <p className="text-sm text-gray-500">{summary.total_calls} görüşme • Oluşturulma: {summary.created_at}</p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${summary.avg_sentiment >= 0.3 ? 'bg-green-100 text-green-700' :
                                        summary.avg_sentiment <= -0.3 ? 'bg-red-100 text-red-700' :
                                            'bg-gray-100 text-gray-700'
                                        }`}>
                                        Ort. Duygu: {summary.avg_sentiment?.toFixed(2)}
                                    </span>
                                </div>

                                {summary.ai_summary && (
                                    <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg">
                                        <p className="text-sm text-gray-700 dark:text-gray-300">{summary.ai_summary}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-3 gap-4">
                                    {summary.top_keywords?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 mb-2">En Çok Kullanılan</p>
                                            <div className="flex flex-wrap gap-1">
                                                {summary.top_keywords.slice(0, 5).map((kw, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                                                        {kw.word || kw}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {summary.emerging_trends?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 mb-2">Trendler</p>
                                            <ul className="text-xs text-gray-600 space-y-1">
                                                {summary.emerging_trends.slice(0, 3).map((t, i) => (
                                                    <li key={i}>• {t}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {summary.opportunities?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 mb-2">Fırsatlar</p>
                                            <ul className="text-xs text-gray-600 space-y-1">
                                                {summary.opportunities.slice(0, 3).map((o, i) => (
                                                    <li key={i}>• {o}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default AIInsightsDashboard;

